import { createAsyncThunk } from '@reduxjs/toolkit';

import { PLAN_TYPE } from '@/const/plans';
import ResponseError from '@/errors/responseError';
import paymentModel, { PRICE_NAMES } from '@/models/payment';
import taxInformationSchema from '@/store/billing/schema';
import { storage } from '@/utils/browser';
import { getPriceById } from '@/utils/price';

import { fetchBillingInfo } from '../profile/thunks';
import { STEPS } from './constants';

const USER_COUNTRY_HEADER_KEY = 'x-user-country';

export const fetchPrices = createAsyncThunk(
	'checkout/fetchPrices',
	async (
		{ country, currency, filterActive, filterService },
		{ rejectWithValue }
	) => {
		try {
			return await paymentModel.getPrices({
				country,
				currency,
				filterActive,
				filterService
			});
		} catch (error) {
			if (error instanceof ResponseError) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export function saveCheckoutData({ priceId, step, paymentHandlerId }) {
	storage.save('checkout', { priceId, step, paymentHandlerId });
}

export function getUserCurrency(user) {
	if (user?.paymentInfo?.currency) {
		return user.paymentInfo.currency;
	}
}

export const getPrices = createAsyncThunk(
	'checkout/getPrices',
	async (payload, { rejectWithValue, getState }) => {
		const { country: selectedCountry, allowSelectedCurrency } = payload;
		const { user, currency: selectedCurrency } = getState().profile;
		const userCurrency = getUserCurrency(user);

		const country =
			selectedCountry || user?.taxInformation?.country || user?.country;

		try {
			const { prices, headers = {} } = await paymentModel.getPrices({
				country,
				currency: allowSelectedCurrency
					? selectedCurrency || userCurrency
					: userCurrency,
				filterActive: true
			});

			const userCountry =
				country || headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() || null;

			const priceCurrency = prices.find(price => price.active)?.currency;

			return { prices, userCountry, priceCurrency };
		} catch (error) {
			if (error instanceof Response) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);

export const initCheckout = createAsyncThunk(
	'checkout/initCheckout',
	async (_, { rejectWithValue, getState, dispatch }) => {
		try {
			const state = getState();

			/*
				When loading checkout without passing through billing page (accessing directly from url)
				we have to know if user has a payment method attached or not, for doing that we
				fetch billing data within this thunk only if it wasn't fetched before.
			*/
			if (!state.profile.fetchBillingInfo.completed) {
				await dispatch(fetchBillingInfo()).unwrap();
			}

			const profileState = state.profile;
			const { taxInformation, country, canPurchaseTrial } =
				profileState.user;
			let step = STEPS.BILLING;

			const checkoutData = storage.get('checkout') || {};

			const userCurrency = getUserCurrency(profileState.user);

			// TODO: set saved payment method and currency subscription
			const { prices, headers = {} } = await paymentModel.getPrices({
				country: taxInformation?.country || country,
				currency: profileState.currency || userCurrency,
				filterActive: false
			});

			const activePrices = prices.filter(price => price.active);

			const userCountry =
				headers[USER_COUNTRY_HEADER_KEY]?.toLowerCase() || null;

			const parsedCheckoutPriceId =
				state.checkout?.priceId || checkoutData.priceId?.toString() || null;

			let validPrice = getPriceById(parsedCheckoutPriceId, activePrices);

			if (validPrice?.name === PLAN_TYPE.TRIAL && !canPurchaseTrial) {
				validPrice = activePrices.find(item => item.main);
			}

			if (!validPrice?.handlerId || !validPrice?.active) {
				const validInactivePrice = getPriceById(
					parsedCheckoutPriceId,
					prices
				);

				validPrice = activePrices.find(
					price => price.name === validInactivePrice?.name
				);
			}

			if (validPrice) {
				step = taxInformationSchema({ sync: true }).isValidSync(
					taxInformation
				)
					? STEPS.PAYMENT
					: STEPS.BILLING;
			}

			const defaultPlan = activePrices.find(price => {
				if (canPurchaseTrial) {
					return price.name === PRICE_NAMES.TRIAL;
				}

				return price.main;
			});

			const priceId = validPrice?.handlerId || defaultPlan?.handlerId;

			const priceCurrency = prices.find(price => price.active)?.currency;

			return {
				priceId,
				paymentHandlerId: checkoutData.paymentHandlerId,
				userCountry,
				activePrices,
				step,
				priceCurrency
			};
		} catch (error) {
			if (error instanceof Response) {
				return rejectWithValue({ status: error.status, data: error.data });
			}

			throw error;
		}
	}
);
